import { Pin } from "lucide-react";
import { required, ShowBase, useRecordContext, useTranslate } from "ra-core";
import { useWatch } from "react-hook-form";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { BooleanInput } from "@/components/admin/boolean-input";
import { CreateButton } from "@/components/admin/create-button";
import { Create } from "@/components/admin/create";
import { DataTable } from "@/components/admin/data-table";
import { Edit } from "@/components/admin/edit";
import { EditButton } from "@/components/admin/edit-button";
import { List } from "@/components/admin/list";
import { MobileRecordCard } from "@/components/admin/mobile-record-card";
import { ReferenceField } from "@/components/admin/reference-field";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SearchInput } from "@/components/admin/search-input";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleForm } from "@/components/admin/simple-form";
import { TextArrayInput } from "@/components/admin/text-array-input";
import { TextInput } from "@/components/admin/text-input";
import { Card, CardContent } from "@/components/ui/card";

import { TopToolbar } from "../../atomic-crm/layout/TopToolbar";
import { Markdown } from "../../atomic-crm/misc/Markdown";
import { findChoice, WIKI_CATEGORIES } from "../common/constants";
import { HubPage, StatusBadge, useFormatDate } from "../common/components";

const filters = [
  <SearchInput key="q" source="title@ilike" alwaysOn />,
  <SelectInput
    key="category"
    source="category"
    choices={WIKI_CATEGORIES}
    emptyText="hub.common.all"
    alwaysOn
  />,
];

const WikiList = () => {
  const formatDate = useFormatDate();
  return (
    <List
      resource="wiki_pages"
      filters={filters}
      sort={{ field: "pinned", order: "DESC" }}
      actions={
        <TopToolbar>
          <CreateButton label="hub.wiki.new" />
        </TopToolbar>
      }
    >
      <DataTable
        rowClick="show"
        bulkActionButtons={false}
        mobileCard={(page) => (
          <MobileRecordCard
            title={
              <span className="flex items-center gap-2">
                {page.pinned && (
                  <Pin className="size-3.5 shrink-0 text-amber-500" />
                )}
                <span className="truncate">{page.title}</span>
              </span>
            }
            trailing={
              <StatusBadge
                choice={findChoice(WIKI_CATEGORIES, page.category)}
              />
            }
            meta={[
              formatDate(page.updated_at),
              page.tags?.length
                ? page.tags.map((t: string) => `#${t}`).join(" ")
                : null,
            ]}
          />
        )}
      >
        <DataTable.Col
          source="title"
          render={(page) => (
            <span className="flex items-center gap-2 font-medium">
              {page.pinned && <Pin className="size-3.5 text-amber-500" />}
              {page.title}
            </span>
          )}
        />
        <DataTable.Col
          source="category"
          render={(page) => (
            <StatusBadge choice={findChoice(WIKI_CATEGORIES, page.category)} />
          )}
        />
        <DataTable.Col
          source="tags"
          render={(page) => (
            <span className="text-xs text-muted-foreground">
              {page.tags?.map((t: string) => `#${t}`).join(" ")}
            </span>
          )}
        />
        <DataTable.Col
          source="updated_at"
          render={(page) => formatDate(page.updated_at)}
        />
      </DataTable>
    </List>
  );
};

const WikiShow = () => (
  <ShowBase resource="wiki_pages">
    <WikiShowContent />
  </ShowBase>
);

const WikiShowContent = () => {
  const page = useRecordContext();
  const translate = useTranslate();
  const formatDate = useFormatDate();
  if (!page) return null;
  return (
    <HubPage
      className="max-w-4xl"
      title={
        <div>
          <h1 className="text-3xl font-semibold">{page.title}</h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <StatusBadge choice={findChoice(WIKI_CATEGORIES, page.category)} />
            {translate("resources.wiki_pages.fields.updated_at")}{" "}
            {formatDate(page.updated_at)}
            {page.company_id != null && (
              <ReferenceField
                source="company_id"
                reference="companies"
                link="show"
              />
            )}
            {page.deal_id != null && (
              <ReferenceField source="deal_id" reference="deals" link="show" />
            )}
          </p>
        </div>
      }
      actions={<EditButton />}
    >
      <Card>
        <CardContent>
          <Markdown className="prose prose-sm max-w-none dark:prose-invert">
            {page.content || ""}
          </Markdown>
        </CardContent>
      </Card>
    </HubPage>
  );
};

const ContentPreview = () => {
  const content = useWatch({ name: "content" }) as string | undefined;
  return (
    <Markdown className="prose prose-sm max-w-none rounded-md border p-3 dark:prose-invert">
      {content || ""}
    </Markdown>
  );
};

const WikiInputs = () => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4">
      <TextInput source="title" validate={required()} />
      <div className="grid gap-4 sm:grid-cols-3 [&>*]:min-w-0 [&>*>*]:min-w-0">
        <SelectInput
          source="category"
          choices={WIKI_CATEGORIES}
          validate={required()}
        />
        <ReferenceInput source="company_id" reference="companies">
          <AutocompleteInput
            label="resources.wiki_pages.fields.company_id"
            optionText="name"
          />
        </ReferenceInput>
        <ReferenceInput source="deal_id" reference="deals">
          <AutocompleteInput
            label="resources.wiki_pages.fields.deal_id"
            optionText="name"
          />
        </ReferenceInput>
      </div>
      <div className="grid gap-4 lg:grid-cols-2 [&>*]:min-w-0 [&>*>*]:min-w-0">
        <TextInput source="content" multiline rows={20} className="font-mono" />
        <div>
          <p className="mb-1 text-sm font-medium">
            {translate("hub.wiki.preview")}
          </p>
          <ContentPreview />
        </div>
      </div>
      <TextArrayInput source="tags" />
      <BooleanInput source="pinned" />
    </div>
  );
};

const WikiCreate = () => (
  <Create resource="wiki_pages" redirect="show" className="w-full">
    <Card>
      <CardContent>
        <SimpleForm
          className="max-w-none"
          defaultValues={{
            category: "general",
            content: "",
            tags: [],
            pinned: false,
          }}
        >
          <WikiInputs />
        </SimpleForm>
      </CardContent>
    </Card>
  </Create>
);

const WikiEdit = () => (
  <Edit
    resource="wiki_pages"
    redirect="show"
    mutationMode="pessimistic"
    className="w-full"
  >
    <Card>
      <CardContent>
        <SimpleForm className="max-w-none">
          <WikiInputs />
        </SimpleForm>
      </CardContent>
    </Card>
  </Edit>
);

export default {
  list: WikiList,
  show: WikiShow,
  create: WikiCreate,
  edit: WikiEdit,
  recordRepresentation: "title",
};
